/**
 * PlanComments: the author's explanation of their plan, for the people
 * reviewing it — which communities it keeps together, why boundaries fall
 * where they do, trade-offs made for population balance.
 *
 * Autosaves after a typing pause (guest drafts save in this browser). On
 * submission the comments travel with the plan; they also appear on the
 * plan's public share page. Read-only once the plan is submitted.
 */
import { PLAN_COMMENTS_MAX } from '../../lib/plans';
import { usePaintingStore } from '../../stores/painting';

const PROMPTS = [
  'Which neighborhoods or communities did you keep together?',
  'Why do the boundaries fall where they do?',
  'Any trade-offs you made to balance population?',
];

function SaveHint(): JSX.Element | null {
  const state = usePaintingStore((s) => s.commentsState);
  const error = usePaintingStore((s) => s.commentsError);
  const isLocalDraft = usePaintingStore((s) => s.isLocalDraft);
  const retry = usePaintingStore((s) => s.saveComments);
  if (state === 'pending' || state === 'saving') return <span className="text-stone-400">Saving…</span>;
  if (state === 'saved') {
    return <span className="text-emerald-700">{isLocalDraft ? 'Saved in this browser' : 'Saved'}</span>;
  }
  if (state === 'error') {
    return (
      <span className="text-red-600" title={error ?? undefined}>
        Not saved —{' '}
        <button type="button" className="font-semibold underline" onClick={() => void retry()}>
          retry
        </button>
      </span>
    );
  }
  return null;
}

export function PlanComments(): JSX.Element {
  const comments = usePaintingStore((s) => s.comments);
  const setComments = usePaintingStore((s) => s.setComments);
  const saveComments = usePaintingStore((s) => s.saveComments);
  const submitted = usePaintingStore((s) => s.planStatus === 'submitted');
  const length = comments.length;

  if (submitted) {
    return (
      <div className="space-y-2">
        <h3 className="panel-title">Comments</h3>
        {comments.trim() ? (
          <p className="whitespace-pre-wrap rounded-xl bg-stone-50 px-3 py-2.5 text-sm leading-relaxed text-stone-700">
            {comments}
          </p>
        ) : (
          <p className="text-xs text-stone-400">No comments were included with this plan.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <label htmlFor="plan-comments" className="flex items-baseline justify-between gap-2">
        <span className="panel-title">Comments</span>
        <span className="text-[11px]">
          <SaveHint />
        </span>
      </label>
      <textarea
        id="plan-comments"
        rows={4}
        maxLength={PLAN_COMMENTS_MAX}
        value={comments}
        onChange={(e) => setComments(e.target.value)}
        onBlur={() => void saveComments()}
        placeholder={PROMPTS.join('\n')}
        aria-describedby="plan-comments-help"
        className="field-input mt-0 resize-y text-sm leading-relaxed"
      />
      <p id="plan-comments-help" className="flex justify-between gap-2 text-[11px] leading-snug text-stone-500">
        <span>Explain your plan to reviewers. Sent with your submission and shown on your share link.</span>
        <span className={`shrink-0 tabular-nums ${length > PLAN_COMMENTS_MAX * 0.9 ? 'text-amber-700' : 'text-stone-400'}`}>
          {length}/{PLAN_COMMENTS_MAX}
        </span>
      </p>
    </div>
  );
}
